<script lang="ts" setup>
// ================================
// IMPORTS
// ================================
import { ref, watch, onMounted } from "vue"
import { useI18n } from "vue-i18n"
import { useBuildingStore } from "@core/stores/building-v2.store"
import type { Building } from "@types"
import type { ItemBuildingData } from "@core/components/item/ItemBuilding.vue"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            label_buildings: "Bâtiments associés",
            buildings_error: "Erreur lors du chargement des bâtiments.",
            selected_label: "{count} bâtiment(s) sélectionné(s)",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** Identifiant de l'entreprise (pour charger les bâtiments) */
        companyId?: string | null
        /** Bâtiments actuellement associés au profil */
        buildingsProfil?: (Building | { _id: string })[]
    }>(),
    {
        companyId: null,
        buildingsProfil: () => [],
    }
)

const emit = defineEmits<{
    "update:buildings": [(Building | { _id: string })[]]
}>()

// ================================
// STORE
// ================================
const buildingStore = useBuildingStore()

// ================================
// STATE
// ================================
/** Liste des bâtiments de l'entreprise (chargée au mount via le store) */
const buildingsList = ref<Building[]>([])
/** Bâtiments sélectionnés (v-model du MultiSelect) */
const selectedBuildings = ref<Building[]>([])
/** Chargement en cours */
const loadingBuildings = ref(false)
/** Message d'erreur affiché */
const errorMessage = ref("")

/** Options du VirtualScroller : ne rend que les options visibles (lazy virtual scroll) */
const virtualScrollerOptions = { itemSize: 52 }

// ================================
// HELPERS
// ================================
/** Convertit un Building en données attendues par ItemBuilding */
function toItemBuildingData(b: Building): ItemBuildingData {
    const addr =
        b.address ?? (b as Building & { adress?: Building["address"] }).adress
    return {
        _id: b._id,
        name: b.name,
        niveau: b.infos?.niveau,
        address: addr
            ? {
                  street: addr.street,
                  zipcode: addr.zipcode,
                  city: addr.city,
              }
            : undefined,
    }
}

/** Synchronise la sélection depuis buildingsProfil vers selectedBuildings */
function syncSelectionFromProfil(): void {
    if (buildingsList.value.length === 0) return
    const ids = new Set(
        (props.buildingsProfil ?? []).map((p) =>
            typeof p === "string" ? p : p._id
        )
    )
    selectedBuildings.value = buildingsList.value.filter((b) => ids.has(b._id))
}

// ================================
// INTERACTIONS
// ================================
/** Émet la sélection courante vers le parent */
function emitSelection(value: Building[] | null): void {
    const list = value ?? []
    emit(
        "update:buildings",
        list.map((b) => ({ _id: b._id }))
    )
}

// ================================
// WATCHERS
// ================================
watch(
    () => [props.buildingsProfil, buildingsList] as const,
    () => syncSelectionFromProfil(),
    { deep: true }
)

// ================================
// LIFECYCLE
// ================================
onMounted(async () => {
    if (!props.companyId) return
    errorMessage.value = ""
    loadingBuildings.value = true
    try {
        buildingsList.value = await buildingStore.fetchBuildingsByCompanyId(
            props.companyId
        )
        syncSelectionFromProfil()
    } catch {
        errorMessage.value = t("buildings_error")
    } finally {
        loadingBuildings.value = false
    }
})
</script>

<template>
    <div class="usage-profile-edit-building flex flex-col gap-4">
        <BaseAppMessage
            v-if="errorMessage"
            severity="error"
            :message="errorMessage"
        />
        <BaseInputWrapper
            :label="t('label_buildings')"
            input-id="buildings-select"
        >
            <BaseMultiSelect
                v-model="selectedBuildings"
                class="w-full"
                :options="buildingsList"
                option-label="name"
                data-key="_id"
                input-id="buildings-select"
                :loading="loadingBuildings"
                :max-selected-labels="3"
                :selected-items-label="
                    t('selected_label', { count: selectedBuildings.length })
                "
                display="chip"
                filter
                filter-placeholder="Rechercher…"
                :virtual-scroller-options="virtualScrollerOptions"
                @update:model-value="emitSelection"
            >
                <template #option="slotProps">
                    <ItemBuilding
                        :building="toItemBuildingData(slotProps.option)"
                    />
                </template>
            </BaseMultiSelect>
        </BaseInputWrapper>
    </div>
</template>
